import { networkApiCall, NetworkUnauthorizedError } from '../lib/networking';
import type { SessionUser } from '../RhinoContext';
import {
  useMutation,
  useQueryClient,
  type QueryClient
} from '@tanstack/react-query';

export const authSessionKey = ['session'];
const SIGN_IN_PATH = '/api/auth/sign_in';
const SIGN_OUT_PATH = '/api/auth/sign_out';
const SESSION_PATH = '/api/auth/validate_token';

// Keep the observed session query alive so route guards see account changes.
// Cancel old requests before removing account data to prevent late responses
// from repopulating another account's cache.
export async function replaceAuthSession<User>(
  client: QueryClient,
  user: User | null
) {
  await client.cancelQueries();
  client.removeQueries({
    predicate: (query) => query.queryKey[0] !== authSessionKey[0]
  });
  client.setQueryData(authSessionKey, user);
}

export function authSessionQueryOptions<User = SessionUser>(
  options: { suppressSessionErrors?: boolean } = {}
) {
  return {
    queryKey: authSessionKey,
    queryFn: async ({
      signal
    }: {
      signal: AbortSignal;
    }): Promise<User | null> => {
      try {
        const response = await networkApiCall(SESSION_PATH, {
          method: 'get',
          signal
        });
        return (response?.data?.data ?? null) as User | null;
      } catch (error) {
        if (
          error instanceof NetworkUnauthorizedError ||
          options.suppressSessionErrors
        )
          return null;
        throw error;
      }
    },
    retry: false as const
  };
}

export function useSignInController<User = SessionUser>(
  options: { onSignedIn?: (user: User) => void | Promise<void> } = {}
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      await client.cancelQueries();
      const response = await networkApiCall(SIGN_IN_PATH, {
        method: 'post',
        data
      });
      const user = response?.data?.data as User | undefined;
      if (!user)
        throw new Error('Sign-in did not return a user. Please try again.');
      await replaceAuthSession(client, user);
      await options.onSignedIn?.(user);
      return response;
    }
  });
}

export function useSignOutController(
  options: { onSignedOut?: () => void | Promise<void> } = {}
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => networkApiCall(SIGN_OUT_PATH, { method: 'delete' }),
    onSuccess: async () => {
      await replaceAuthSession(client, null);
      await options.onSignedOut?.();
    },
    onError: async (error) => {
      // A revoked/expired session is already signed out. A network failure is
      // not: retain the account and its data so the user can retry.
      if (error instanceof NetworkUnauthorizedError) {
        await replaceAuthSession(client, null);
        await options.onSignedOut?.();
      }
    }
  });
}
